import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import { Check, Timer, MapPin, Phone as PhoneIcon, Bell } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import apiClient from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { BrandLogo } from '../components/BrandLogo';
import {
  activeKitchenOrders,
  applyKitchenOrderEvent,
  createKitchenOrdersState,
  reconcileKitchenSnapshot,
} from './kitchenOrders';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';
const NEW_ORDER_HIGHLIGHT_MS = 12000;

// Short two-note chime via the Web Audio API — no audio asset to ship/host,
// and it still fires even if the tab has no <audio> element anywhere.
const playNewOrderChime = () => {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    const ctx = new AudioCtx();
    const beep = (freq: number, startTime: number, duration: number) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, startTime);
      gain.gain.exponentialRampToValueAtTime(0.35, startTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    const now = ctx.currentTime;
    beep(880, now, 0.18);
    beep(1174.66, now + 0.2, 0.28);
    setTimeout(() => ctx.close(), 700);
  } catch {
    // Web Audio unavailable/blocked (e.g. no user interaction yet) — the
    // visual highlight still carries the alert on its own.
  }
};

const KitchenTablet = () => {
  const { token, logout } = useAuth();
  const { showToast } = useToast();
  const [orderState, setOrderState] = useState(createKitchenOrdersState);
  const orderStateRef = useRef(orderState);
  const socketConnected = useRef(false);
  const orders = activeKitchenOrders(orderState);
  const [now, setNow] = useState(() => Date.now());
  const [newOrderIds, setNewOrderIds] = useState<Set<string>>(new Set());
  // Distinguishes loading, live, stale (socket down) and failure so staff can
  // tell whether the board can be trusted.
  const [dataState, setDataState] = useState<'loading' | 'live' | 'stale' | 'error'>('loading');
  const highlightTimeouts = useRef<ReturnType<typeof setTimeout>[]>([]);
  const updateOrderState = (update: (current: typeof orderState) => typeof orderState) => {
    const next = update(orderStateRef.current);
    orderStateRef.current = next;
    setOrderState(next);
  };

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timeouts = highlightTimeouts.current;
    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    if (!token) return;

    fetchOrders();

    const socket = io(SOCKET_URL, { auth: { token } });

    // A new connection is not trustworthy until its authoritative snapshot has
    // reconciled successfully. `connect` also fires after Socket.IO reconnects.
    socket.on('connect', () => {
      socketConnected.current = true;
      fetchOrders();
    });
    socket.on('disconnect', () => {
      socketConnected.current = false;
      setDataState('stale');
    });
    // A revoked session (passcode rotation) or expired token must not silently
    // retry forever: force re-authentication.
    socket.on('sessionRevoked', () => { showToast('Sesión revocada. Inicia sesión de nuevo.', 'error'); logout(); });
    socket.on('connect_error', (err: any) => {
      if (String(err?.message).includes('unauthorized')) {
        showToast('Sesión inválida. Inicia sesión de nuevo.', 'error');
        logout();
      }
    });

    socket.on('newOrder', (newOrder) => {
      updateOrderState(prev => applyKitchenOrderEvent(prev, newOrder));

      playNewOrderChime();
      const orderLabel = newOrder.orderNumber ? `#${newOrder.orderNumber}` : (newOrder.delivery?.customerName || newOrder.tableNumber || newOrder.customerName || 'Sin nombre');
      showToast(`Nuevo pedido ${orderLabel}`, 'info');

      setNewOrderIds(prev => new Set(prev).add(newOrder._id));
      const timeoutId = setTimeout(() => {
        setNewOrderIds(prev => {
          const next = new Set(prev);
          next.delete(newOrder._id);
          return next;
        });
      }, NEW_ORDER_HIGHLIGHT_MS);
      highlightTimeouts.current.push(timeoutId);
    });

    socket.on('orderUpdated', (updatedOrder) => {
      updateOrderState(prev => applyKitchenOrderEvent(prev, updatedOrder));
    });

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchSeq = useRef(0);

  const fetchOrders = async () => {
    const seq = ++fetchSeq.current;
    const requestEventSeq = orderStateRef.current.eventSeq;
    try {
      const res = await apiClient.get('/orders', { params: { status: 'pendiente,preparando,listo' } });
      if (seq !== fetchSeq.current) return; // a newer snapshot superseded this one
      updateOrderState(prev => reconcileKitchenSnapshot(prev, res.data, requestEventSeq));
      setDataState(socketConnected.current ? 'live' : 'stale');
    } catch (error) {
      if (seq !== fetchSeq.current) return;
      console.error('Error fetching orders:', error);
      setDataState('error');
    }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      const res = await apiClient.patch(`/orders/${id}/status`, { status });
      // The accepted PATCH result is authoritative; never wait on the echo.
      if (res.data) updateOrderState(prev => applyKitchenOrderEvent(prev, res.data));
    } catch (error: any) {
      if (error?.response?.status === 409) {
        showToast(error.response?.data?.message || 'El pedido cambió. Actualizando…', 'error');
        fetchOrders();
      } else {
        console.error('Error updating status:', error);
      }
    }
  };

  const getElapsedTime = (startTime: string) => {
    const start = new Date(startTime).getTime();
    const diff = Math.floor((now - start) / 1000 / 60);
    return diff;
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-8">
      {dataState !== 'live' && (
        <div role="status" className={`mb-6 rounded-2xl px-6 py-3 text-sm font-bold border ${
          dataState === 'loading' ? 'bg-slate-800/60 border-slate-700 text-slate-300' :
          dataState === 'stale' ? 'bg-amber-500/10 border-amber-500/40 text-amber-300' :
          'bg-red-500/10 border-red-500/40 text-red-300'
        }`}>
          {dataState === 'loading' && 'Cargando pedidos…'}
          {dataState === 'stale' && 'Conexión en tiempo real perdida: la pantalla puede estar desactualizada. Reconectando…'}
          {dataState === 'error' && (
            <span className="flex items-center justify-between gap-4">
              No se pudieron cargar los pedidos. Verifica la conexión con el servidor.
              <Button onClick={fetchOrders} className="shrink-0">Reintentar</Button>
            </span>
          )}
        </div>
      )}
      <header className="flex flex-col md:flex-row justify-between items-center mb-12 gap-6">
        <div className="flex items-center gap-4">
          <BrandLogo className="h-16 w-16" />
          <div>
            <h1 className="text-4xl font-black tracking-tight">PARRILLA</h1>
            <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">Choripan Nea • Kitchen View</p>
          </div>
        </div>
        <div className="flex gap-8 bg-slate-900/50 backdrop-blur-md px-8 py-4 rounded-3xl border border-slate-800">
          <div className="text-center">
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Activos</p>
            <p className="text-2xl font-black text-primary">{orders.length}</p>
          </div>
          <div className="w-px bg-slate-800" />
          <div className="text-center">
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Promedio</p>
            <p className="text-2xl font-black text-white">12'</p>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
        <AnimatePresence mode='popLayout'>
          {orders.map(order => {
            const time = getElapsedTime(order.createdAt);
            const isUrgent = time > 15;
            const isNew = newOrderIds.has(order._id);

            return (
              <motion.div
                layout
                key={order._id}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
              >
                <Card className={`
                  relative overflow-hidden border-t-8 p-6
                  ${order.status === 'pendiente' ? 'border-t-red-500' : 'border-t-primary'}
                  ${isUrgent ? 'animate-pulse shadow-2xl shadow-red-500/20' : ''}
                  ${isNew ? 'ring-4 ring-primary shadow-2xl shadow-primary/40' : ''}
                `}>
                  {isNew && (
                    <div className="absolute top-0 left-0 right-0 flex items-center justify-center gap-1.5 bg-primary text-white text-[10px] font-black uppercase tracking-widest py-1.5 z-10">
                      <Bell size={12} className="animate-pulse" />
                      Nuevo pedido
                    </div>
                  )}
                  <>
                    {order.orderNumber && (
                      <div className={`flex items-baseline gap-1.5 mb-3 ${isNew ? 'mt-6' : ''}`}>
                        <span className="text-slate-500 text-lg font-black">#</span>
                        <span className="text-4xl font-black text-primary leading-none tracking-tight">{order.orderNumber}</span>
                      </div>
                    )}
                    <div className={`flex justify-between items-start mb-4 ${isNew && !order.orderNumber ? 'mt-6' : ''}`}>
                    <div className="flex-1 min-w-0 mr-3">
                      <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">
                        {order.delivery ? 'Delivery' : 'Mesa / Cliente'}
                      </p>
                      <h2 className="text-xl font-black truncate">
                        {order.delivery?.customerName || order.tableNumber || order.customerName || 'Sin nombre'}
                      </h2>
                      {order.delivery && (
                        <div className="mt-2 space-y-1.5">
                          <div className="flex items-center gap-2 text-slate-400">
                            <PhoneIcon size={12} className="flex-shrink-0" />
                            <span className="text-xs font-bold">{order.delivery.phone}</span>
                          </div>
                          <div className="flex items-start gap-2 text-slate-400">
                            <MapPin size={12} className="flex-shrink-0 mt-0.5" />
                            <span className="text-[11px] leading-snug line-clamp-2">{order.delivery.address}</span>
                          </div>
                          {order.delivery.addressNotes && (
                            <p className="text-[10px] text-slate-500 italic pl-5">{order.delivery.addressNotes}</p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className={`
                      flex items-center gap-2 px-3 py-1.5 rounded-xl font-black text-sm
                      ${isUrgent ? 'bg-red-500 text-white' : 'bg-slate-100 dark:bg-slate-900 text-primary'}
                    `}>
                      <Timer size={16} />
                      {time}'
                    </div>
                  </div>

                  <div className="space-y-3 mb-8 min-h-[120px]">
                    {order.items.map((item: any, idx: number) => {
                      const selections = item.selectedOptions instanceof Map
                        ? Object.fromEntries(item.selectedOptions)
                        : (item.selectedOptions || {});
                      const productOptions: any[] = item.product?.options || [];

                      const gridEntries: Array<{ key: string; val: string; icon?: string }> = [];
                      const pillEntries: Array<{ key: string; val: string; icon?: string }> = [];

                      Object.entries(selections).forEach(([key, val]) => {
                        const meta = productOptions.find((o: any) => o.name === key);
                        const icon = meta?.icons?.[val as string];
                        if (meta?.displayStyle === 'grid') {
                          gridEntries.push({ key, val: val as string, icon });
                        } else {
                          pillEntries.push({ key, val: val as string, icon });
                        }
                      });

                      return (
                        <div key={idx} className="rounded-xl bg-slate-50 dark:bg-slate-900/60 p-4 border border-slate-100 dark:border-slate-800">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="h-10 w-10 flex-shrink-0 bg-primary text-white rounded-xl flex items-center justify-center font-black text-xl">
                              {item.quantity}
                            </span>
                            <span className="text-base font-bold text-slate-700 dark:text-slate-300">
                              {item.name || item.product?.name || 'Producto eliminado'}
                            </span>
                          </div>
                          {gridEntries.map(({ key, val, icon }) => (
                            <div key={key} className="flex items-center gap-3 bg-orange-500 text-white px-4 py-3 rounded-xl mt-2">
                              {icon && (
                                icon.startsWith('http')
                                  ? <img src={icon} alt={val} className="h-8 w-8 object-contain" />
                                  : <span className="text-2xl">{icon}</span>
                              )}
                              <span className="text-xl font-black tracking-wide uppercase">{val}</span>
                            </div>
                          ))}
                          {pillEntries.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {pillEntries.map(({ key, val, icon }) => (
                                <span key={key} className="text-xs bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-3 py-1 rounded-full font-bold inline-flex items-center gap-1">
                                  {icon && (
                                    icon.startsWith('http')
                                      ? <img src={icon} alt={val} className="h-4 w-4 object-contain" />
                                      : <span>{icon}</span>
                                  )}{val}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex gap-3">
                    {order.status === 'pendiente' ? (
                      <Button
                        onClick={() => updateStatus(order._id, 'preparando')}
                        className="flex-1 h-14 rounded-2xl text-lg font-black"
                      >
                        Empezar
                      </Button>
                    ) : order.status === 'preparando' ? (
                      <Button
                        onClick={() => updateStatus(order._id, 'listo')}
                        className="flex-1 h-14 rounded-2xl text-lg font-black bg-green-500 hover:bg-green-600 border-none"
                      >
                        <Check size={24} className="mr-2" />
                        Listo
                      </Button>
                    ) : (
                      <Button
                        onClick={() => updateStatus(order._id, 'entregado')}
                        className="flex-1 h-14 rounded-2xl text-lg font-black bg-slate-700 hover:bg-slate-600 border-none"
                      >
                        <Check size={24} className="mr-2" />
                        Entregado
                      </Button>
                    )}
                  </div>
                  </>
                </Card>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default KitchenTablet;
